import { VIEW_IDS } from "../../constants";

const transformOrganizationInfo = prospect => {
  const {
    industryMultiSelect,
    numberOfEmployees,
    organisationContactDetails,
    ...rest
  } = prospect.organizationInfo;
  const { industry, subCategory } = (industryMultiSelect || []).reduce(
    (industryAndSubCategory, item) => {
      item.industry?.length && industryAndSubCategory.industry.push(item.industry[0]);
      item.subCategory?.length && industryAndSubCategory.subCategory.push(item.subCategory[0]);
      return industryAndSubCategory;
    },
    { industry: [], subCategory: [] }
  );
  return {
    ...rest,
    industry,
    subCategory,
    numberOfEmployees,
    organisationContactDetails
  };
};

const transformApplicationInfo = prospect => {
  const { accountType, rakValuePackage, islamicBanking } = prospect.applicationInfo;
  return { accountType, rakValuePackage, islamicBanking };
};

const transformSignatoryInfo = (
  prospect,
  viewId,
  currentIndex,
  isMultiPartnerJourney,
  isEditable,
  isMultipleSignatoriesJourney
) => {
  const signatory = prospect.signatoryInfo[currentIndex];
  const signatoryList = prospect.signatoryInfo.map(signatory => {
    const {
      isSignatoryEmailIdChanged,
      isSignatoryMobileNumberChanged,
      ...cleanedSignatory
    } = signatory;
    return cleanedSignatory;
  });

  switch (viewId) {
    case "/ConsentInfo":
      return [
        {
          consentInfo: signatory.consentInfo,
          signatoryId: signatory.signatoryId
        }
      ];
    case "/StakeholdersAdditionalInfo": {
      const {
        stakeholderAdditionalInfo,
        showSOF,
        signatoryId,
        kycDetails,
        debitCardInfo
      } = signatory;
      return [
        {
          stakeholderAdditionalInfo,
          showSOF,
          signatoryId,
          kycDetails,
          ...(isMultipleSignatoriesJourney && kycDetails.isSignatory && { debitCardInfo })
        }
      ];
    }
    case "/AccountInfo":
      return [
        {
          debitCardInfo: signatory?.debitCardInfo,
          signatoryId: signatory?.signatoryId
        }
      ];
    case "/StakeholdersInfo": {
      if (signatory) {
        const {
          isEFRCheckLimitExceeded,
          signatoryId,
          eFRCompletedTimestamp,
          consentInfo,
          kycDetails
        } = signatory;
        const kycSignatoryDetails = {
          kycDetails: {
            isPrimaryContact: isMultiPartnerJourney ? kycDetails?.isPrimaryContact : true,
            isSignatory: isMultiPartnerJourney ? kycDetails?.isSignatory : true
          }
        };
        if (isEFRCheckLimitExceeded) {
          return [
            {
              isEFRCheckLimitExceeded,
              signatoryId,
              ...kycSignatoryDetails,
              ...(isMultiPartnerJourney && { isApplicationFilled: true })
            }
          ];
        } else if (eFRCompletedTimestamp || consentInfo) {
          return [
            {
              eFRCompletedTimestamp,
              consentInfo,
              signatoryId,
              ...kycSignatoryDetails,
              ...(isMultiPartnerJourney && { isApplicationFilled: true })
            }
          ];
        }
      }
      break;
    }
    case "/StakeholdersInfoPreview": {
      const {
        editedFullName,
        firstName,
        middleName,
        lastName,
        debitCardInfo,
        mothersMaidenName,
        kycDetails,
        isEFRDataCorrect,
        countryofBirth,
        maritalStatus,
        editedEmailId,
        editedMobileNumber,
        contactDetails,
        signatoryId,
        isSignatureVerified
      } = signatory;

      const baseData = {
        editedFullName,
        debitCardInfo,
        mothersMaidenName,
        kycDetails,
        isEFRDataCorrect,
        countryofBirth,
        maritalStatus,
        signatoryId,
        firstName,
        middleName,
        lastName,
        isSignatureVerified,
        ...(isMultiPartnerJourney && { isApplicationFilled: true })
      };

      if (!isMultiPartnerJourney) {
        kycDetails["isSignatory"] = true;
        kycDetails["isShareholder"] = true;
        kycDetails["shareHoldingPercentage"] = "100.00";
        kycDetails["isPrimaryContact"] = true;
      }

      const additionalData = isEditable
        ? {}
        : {
            editedEmailId,
            editedMobileNumber,
            ...(isMultiPartnerJourney && { contactDetails })
          };

      return [
        {
          ...baseData,
          ...additionalData
        }
      ];
    }
    case "/PartnerDetailsInfo":
    case "/PartnershipSummaryInfo":
      return signatoryList;
    default: {
      const {
        editedFullName,
        debitCardInfo,
        mothersMaidenName,
        kycDetails,
        isEFRDataCorrect,
        countryofBirth,
        editedEmailId,
        editedMobileNumber,
        contactDetails,
        signatoryId
      } = signatory;
      return [
        {
          editedFullName,
          debitCardInfo,
          mothersMaidenName,
          kycDetails,
          isEFRDataCorrect,
          countryofBirth,
          editedEmailId,
          editedMobileNumber,
          ...(isMultiPartnerJourney && { contactDetails, signatoryId })
        }
      ];
    }
  }
};

const transformApplicantInfo = (prospect, viewId) => {
  if (viewId === "/AccountInfo" || viewId === "/SubmitApplication") {
    const { roCode, allianceCode, sourcingId } = prospect.applicantInfo;
    return { roCode, allianceCode, sourcingId };
  }
  return prospect.applicantInfo;
};

const transformAccountInfo = (prospect, isIslamicBanking, isCrossSellFeatureEnabled) => {
  const { receiveInterest, paymentSolutionList, creditCardList, ...rest } = prospect.accountInfo;
  if (isIslamicBanking) {
    return {
      ...rest,
      receiveInterest: false,
      ...(isCrossSellFeatureEnabled && { paymentSolutionList, creditCardList })
    };
  }
  return {
    ...rest,
    receiveInterest,
    ...(isCrossSellFeatureEnabled && { paymentSolutionList, creditCardList })
  };
};

const transformDocuments = (prospect, currentIndex) => {
  const { stakeholdersDocuments, ...rest } = prospect.documents;

  if (!stakeholdersDocuments) {
    return prospect.documents;
  }

  // Filter stakeholder documents where key starts with currentIndex_
  const filteredStakeholdersDocuments = {};
  const currentIndexPrefix = `${currentIndex}_`;

  Object.keys(stakeholdersDocuments).forEach(key => {
    if (key.startsWith(currentIndexPrefix)) {
      filteredStakeholdersDocuments[key] = stakeholdersDocuments[key];
    }
  });

  return {
    ...rest,
    stakeholdersDocuments: filteredStakeholdersDocuments
  };
};

const transformCompanyAdditionalInfo = (prospect, isTaxInfoKycRemediationEnabled, isAgent) => {
  const {
    vatRegistrationNo,
    uaeTinNo,
    corporateTaxRegistrationDate,
    subsidiariesDetails,
    ...rest
  } = prospect.companyAdditionalInfo;
  return {
    ...rest,
    ...(isTaxInfoKycRemediationEnabled && {
      vatRegistrationNo,
      uaeTinNo,
      corporateTaxRegistrationDate
    }),
    ...(isAgent && { subsidiariesDetails })
  };
};

const transformDulLicenseInfo = prospect => {
  const {
    dulJourneyType,
    isDulSelected,
    isManualJourneySelected,
    isDulConsentRecieved
  } = prospect.dulLicenseInfo;

  return { dulJourneyType, isDulSelected, isManualJourneySelected, isDulConsentRecieved };
};

const transformProspectData = ({
  prospect,
  key,
  viewId,
  currentIndex,
  isMultiPartnerJourney,
  isEditable,
  isIslamicBanking,
  isCrossSellFeatureEnabled,
  isTaxInfoKycRemediationEnabled,
  isAgent,
  isMultipleSignatoriesJourney,
  isUpdateSignatory
}) => {
  switch (key) {
    case "organizationInfo":
      return transformOrganizationInfo(prospect);
    case "applicationInfo":
      return transformApplicationInfo(prospect);
    case "signatoryInfo":
      return transformSignatoryInfo(
        prospect,
        viewId,
        currentIndex,
        isMultiPartnerJourney,
        isEditable,
        isMultipleSignatoriesJourney
      );
    case "kycAnnexure":
      return isAgent || viewId === VIEW_IDS.CompanyAdditionalInfo
        ? prospect.kycAnnexure
        : undefined;
    case "applicantInfo":
      return transformApplicantInfo(prospect, viewId);
    case "accountInfo":
      return transformAccountInfo(prospect, isIslamicBanking, isCrossSellFeatureEnabled);
    case "companyAdditionalInfo":
      return transformCompanyAdditionalInfo(prospect, isTaxInfoKycRemediationEnabled, isAgent);
    case "documents": {
      const documents = isUpdateSignatory
        ? transformDocuments(prospect, currentIndex)
        : prospect[key];
      return (
        documents && {
          ...documents,
          companyDocuments: Array.isArray(documents.companyDocuments)
            ? documents.companyDocuments
            : []
        }
      );
    }
    case "dulLicenseInfo":
      return transformDulLicenseInfo(prospect);
    case "sid":
      if (viewId === "/PartnershipSummaryInfo" || viewId === "/PartnerDetailsInfo") {
        return prospect[key];
      }
      break;
    default:
      return prospect[key];
  }
};

export { transformProspectData };
